import { Dispatch, SetStateAction } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import { Box, Button, Grid, Typography, useMediaQuery } from '@mui/material';
import {
  ChipBadge,
  ContentDetailCard,
  IStyles,
  ResourceStatus,
  IconWrapper,
  LockToggleButton,
  NoContentCard,
  SecondaryButton,
  SectionListWithTopCarousel,
  deserify,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  resourceTypeName,
  theme,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import {
  ResourceInfo,
  SubjectChapterMiniInfo,
  TopicMiniInfoV2,
} from '@protos/content_management/content.common.apis_pb';
import {
  ApprovalInfo,
  ContentApprovalStatusEnum,
  Resource_ResourceEnum,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import { ContentLockModuleData } from '@protos/learning_management/lms.common.apis_pb';
import {
  ContentLockStatusType,
  Feature,
} from '@protos/school_management/school.db_pb';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { RESOURCE_UPLOAD } from '../../../../routeHandling/RoutesNomenclature';
import { onResourceClick } from '../../../../utils/resource';
import { interactionEvent } from '../../../Auth/auth.events';
import { ResourceShimmer } from '../../LessonPlanLists/shimmer';
import { setCustomResourceData } from '../../reducer/teach.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import {
  ChapterPerformanceStats,
  LearningOutcomes,
  NCFGoal,
} from '@protos/analysis_management/analysis.teacher.apis_pb';
import { PieChart } from '../../../ManageHomework/CompletedHomework/components/PieChart';

const styles: IStyles = {
  resourceContainer: {
    flexBasis: { xs: '100%', md: pxTovW(200), lg: pxTovW(862) },
    marginBottom: { xs: pxToRem(43), md: 0 },
  },

  chapterAnalysis: {
    // display: 'flex',
    width: { xs: '90%', md: '100%' },
    boxSizing: 'border-box',
    flexDirection: { xs: 'column', md: 'row' },
    padding: { xs: pxToRem(10), md: 0 },
    gap: pxToRem(10),
    // backgroundColor: 'red',
    boxShadow: '0px 0px 6px #00000029',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    paddingX: { xs: pxToRem(25), md: pxTovW(30) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    pb: { xs: pxToRem(20), md: pxTovW(37) },
    margin: 'auto',
    marginTop: { xs: pxToRem(20), md: 'unset' },
  },
  chartAndButtonsBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: { xs: pxToRem(20), md: pxTovW(25) },
  },
  chartDetailsBox: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    alignItems: { xs: 'center', md: 'flex-start' },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    // flexDirection: 'column',
    justifyContent: 'space-between',
    border: '1px solid #00000029',
    borderRadius: pxTovW(10),
    width: { xs: '100%', md: pxToRem(90), lg: pxTovW(250) },
    height: { xs: pxToRem(220), md: pxToRem(170), lg: pxTovW(180) },
    paddingX: { xs: pxToRem(10), md: pxTovW(5), lg: pxTovW(15) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    alignItems: 'end',
  },
  viewButtons: {
    display: 'flex',
    height: 'max-content',
    boxSizing: 'border-box',
    gap: pxToRem(10),
    padding: pxToRem(5),
    width: 'max-content',
    backgroundColor: '#F0FDE0',
    border: '1px solid #5DC152',
  },
  bloomLevelText: {
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(10) },
    justifyContent: 'center',
    alignItems: 'center',
  },

  countAndUpload: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingX: { xs: pxToRem(20), md: pxTovW(0) },
    boxSizing: 'border-box',
    mt: { xs: pxToRem(20), md: pxTovW(50) },
    mb: { xs: pxToRem(20), md: pxTovW(0) },
  },
  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(14), md: pxTovW(10) },
    // paddingY: { xs: pxToRem(20), md: pxTovW(0) },
  },
  mock: {},
};

interface DataForDelete {
  resourceId: string;
  resourceTitle: string;
}

interface IProps {
  analysisdata?: {
    chapterPerformance: ChapterPerformanceStats;
    learningOutcomes: LearningOutcomes[];
  };
  ncfData?: NCFGoal[];
  setSelectTopicPopup: Dispatch<SetStateAction<boolean>>;
  setNcfMappingPopup: Dispatch<SetStateAction<boolean>>;
  setLearningOutcomesData: Dispatch<
    SetStateAction<
      | {
          title: string;
          LearningOutcomes: LearningOutcomes[];
        }
      | undefined
    >
  >;
  setLearningOutcomePopup: Dispatch<SetStateAction<boolean>>;
  resourceLoading: 'loading' | 'error' | 'completed';
  setRejectedResourcePopupOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedDataForDelete: Dispatch<SetStateAction<DataForDelete | undefined>>;
  setApprovalInfo: Dispatch<SetStateAction<ApprovalInfo | undefined>>;
  isContentLockFeature: boolean;
  contentLockData: ContentLockModuleData | undefined;
  handleOpenPopup: (
    ev?: React.MouseEvent,
    topicData?: TopicMiniInfoV2,
    categories?: ResourceInfo
  ) => void;
  editResource: (editProps: {
    resourceId: string;
    coverTitle: string;
  }) => Promise<void>;
  setConfirmDeletePopup: Dispatch<SetStateAction<boolean>>;
}

export const RightPanelTeachTopicSelection = (props: IProps) => {
  const {
    analysisdata,
    ncfData,
    setSelectTopicPopup,
    setNcfMappingPopup,
    setLearningOutcomesData,
    setLearningOutcomePopup,
    resourceLoading,
    setRejectedResourcePopupOpen,
    setSelectedDataForDelete,
    setApprovalInfo,
    isContentLockFeature,
    contentLockData,
    handleOpenPopup,
    editResource,
    setConfirmDeletePopup,
  } = props;

  const { isIpadOnly } = useGlobalContext();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const { subject_id, chapter_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { chapter_resources, custom_resource_data, subject_chapter_info } =
    deserify(useAppSelector((state) => state.teach));

  const getBloomLearningOutcomes = (
    learningOutcomes: LearningOutcomes[],
    bloomlevel: string
  ) => {
    const retValue = learningOutcomes.filter((e) => {
      return e.learingOutcomeType === bloomlevel;
    });
    return retValue;
  };

  // Getting Data for ChapterSelectedCard
  const findSelectedChapterData = () => {
    const selectedChapter = subject_chapter_info?.chapters.find(
      (chapter: SubjectChapterMiniInfo) =>
        chapter_id && +chapter_id === chapter.chapterId
    );
    if (selectedChapter) {
      return {
        name: selectedChapter.chapterTitle,
        image: getMediaBasePath(
          selectedChapter.posterImagesUrl,
          'processedMediaBucket'
        ),
      };
    }
    return { name: '', image: '' };
  };
  const hasLearningOutcomes = analysisdata?.learningOutcomes?.some((outcome) =>
    BloomLevelBreakdown.some((b) => outcome.learingOutcomeType === b.title)
  );
  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Box>
        <Box sx={styles.chapterAnalysis}>
          <Typography
            variant={isMobile ? 'h1' : isIpadOnly ? 'h4' : 'h3'}
            fontWeight="bold"
          >
            Chapter Score
          </Typography>

          <Box sx={styles.chartAndButtonsBox}>
            <Box>
              <PieChart
                variant={isMobile ? 'small' : 'fullWidth'}
                color="#5DC152"
                obtainedMarks={
                  analysisdata?.chapterPerformance.scorePercent || 0
                }
                maxMarks={100}
                pieWidth={isMobile ? '75px' : isIpad ? '120px' : '148px'}
                pieHeight={isMobile ? '75px' : isIpad ? '120px' : '148px'}
              />
            </Box>

            <Box sx={styles.buttonsContainer}>
              {ncfData && ncfData.length > 0 && (
                <SecondaryButton
                  variant="outlined"
                  onClick={async () => {
                    setNcfMappingPopup(true);
                    await interactionEvent({
                      context: 'Chapter_score',
                      name: 'NCF_MAPPING',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                  sx={{
                    ...styles.viewButtons,
                    backgroundColor: '#DFEDFD',
                    border: '1px solid #007CDC',
                  }}
                >
                  <Typography
                    variant={isMobile ? 'h4' : 'smallText'}
                    fontWeight="bold"
                    color="primary.main"
                  >
                    NCF Mapping
                  </Typography>
                  <ArrowForwardIosIcon
                    sx={{
                      color: '#007CDC',
                      height: pxTovW(10),
                      width: pxTovW(12),
                    }}
                  />
                </SecondaryButton>
              )}
              {analysisdata?.chapterPerformance.topicsPerformance &&
              analysisdata?.chapterPerformance.topicsPerformance.length > 0 ? (
                <SecondaryButton
                  variant="outlined"
                  onClick={async () => {
                    setSelectTopicPopup(true);
                    await interactionEvent({
                      context: 'Chapter_score',
                      name: 'VIEW_TOPIC_SCORE',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                  sx={styles.viewButtons}
                >
                  <Typography
                    variant={isMobile ? 'h4' : 'smallText'}
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    View Topic Scores
                  </Typography>
                  <ArrowForwardIosIcon
                    sx={{
                      color: '#0AA34F',
                      height: pxTovW(10),
                      width: pxTovW(12),
                    }}
                  />
                </SecondaryButton>
              ) : null}
            </Box>
          </Box>
          {analysisdata?.learningOutcomes && hasLearningOutcomes ? (
            <>
              <Typography
                variant={isMobile ? 'h1' : isIpadOnly ? 'h4' : 'h3'}
                fontWeight="bold"
              >
                Learning Outcomes
              </Typography>

              <Grid container>
                {BloomLevelBreakdown.map(
                  (e, index) =>
                    getBloomLearningOutcomes(
                      analysisdata?.learningOutcomes,
                      e.title
                    ).length > 0 && (
                      <Grid
                        item
                        xs={6}
                        md={6}
                        key={index}
                        sx={{
                          paddingX: { xs: pxToRem(10), md: pxTovW(30) },
                          mt: { xs: pxToRem(10), md: pxTovW(20) },
                          borderRight:
                            index % 2 === 0 ? '1px dashed #BBB9B9' : 'none',
                        }}
                        onClick={() => {
                          const temp =
                            analysisdata?.learningOutcomes &&
                            getBloomLearningOutcomes(
                              analysisdata?.learningOutcomes,
                              e.title
                            );
                          setLearningOutcomesData({
                            title: e.title,
                            LearningOutcomes: temp || [],
                          });
                          setLearningOutcomePopup(true);
                        }}
                      >
                        <Grid
                          container
                          gap={1}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Grid item xs={10} md={9} sx={styles.bloomLevelText}>
                            <Typography
                              variant={isIpadOnly ? 'h3' : 'h2'}
                              fontWeight="bold"
                              color="neutral.royalBlue"
                              sx={{
                                fontSize: { xs: pxToRem(18), md: pxTovW(30) },
                              }}
                            >
                              {analysisdata?.learningOutcomes &&
                                getBloomLearningOutcomes(
                                  analysisdata?.learningOutcomes,
                                  e.title
                                ).length}
                            </Typography>
                            <Typography
                              variant={
                                isMobile ? 'h4' : isIpadOnly ? 'h5' : 'h5'
                              }
                              fontWeight="regular"
                            >
                              {e.title}
                            </Typography>
                          </Grid>

                          <Grid item xs={1} md={2}>
                            <IconWrapper
                              name="arrow"
                              type="png"
                              parentFolder="icons"
                              customSx={{
                                width: { xs: pxToRem(15), lg: pxTovW(22) },
                                cursor: 'pointer',
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    )
                )}
              </Grid>
            </>
          ) : null}
        </Box>
      </Box>

      <Box>
        <Box sx={styles.countAndUpload}>
          <Box sx={styles.textWithBadge}>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(25) } }}
            >
              Chapter Resources
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(14) } }}
            >
              <ChipBadge
                label={
                  chapter_resources?.categoryResourceMap
                    ?.map((obj) => obj.categoryResources.length)
                    .reduce((a, b) => a + b, 0) || 0
                }
                color="primary"
                size="small"
              />
            </Typography>
          </Box>

          {/* FEATURE_CONTENT_UPLOAD not there in Feature */}
          {useFeatureEnabled(
            user_info?.schoolDetails[0]?.featuresPlanInfo,
            Feature.CONTENT_UPLOAD
          ) && (
            <Button
              onClick={async () => {
                dispatch(
                  setCustomResourceData({
                    ...custom_resource_data,
                    selected_chapter: {
                      chapter_name: findSelectedChapterData().name,
                      chapter_id: Number(chapter_id),
                    },
                  })
                );
                navigate(`${RESOURCE_UPLOAD}/${chapter_id}`);
                await interactionEvent({
                  context: 'Chapter_resource',
                  name: 'UPLOAD',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              sx={{
                // width: { xs: pxToRem(81), md: pxTovW(120) },
                // height: { xs: pxToRem(26), md: pxTovW(40) },
                borderRadius: { xs: pxToRem(5), md: pxTovW(30) },
                // marginRight: { xs: pxToRem(10) },
                p: { xs: pxToRem(8), md: pxTovW(8) },
                pr: { xs: pxToRem(8), md: pxTovW(18) },
                display: 'flex',
                gap: { xs: pxToRem(5), md: pxTovW(6) },
                alignSelf: 'center',
                background: '#0AA34F',
                '&:hover': { background: '#0AA34F' },
              }}
            >
              <IconWrapper
                name={isMobile ? 'upload-plain-white' : 'upload'}
                size="md"
                parentFolder="icons"
                type="png"
              />

              {!isMobile && (
                <Typography
                  fontWeight="medium"
                  sx={{ fontSize: { md: pxTovW(16) }, color: 'common.white' }}
                >
                  Upload
                </Typography>
              )}
            </Button>
          )}
        </Box>
        {chapter_resources &&
        chapter_resources.categoryResourceMap.length > 0 ? ( //Temporarily added condition to check if resources are present other dont show any content
          resourceLoading === 'loading' ? (
            <ResourceShimmer />
          ) : resourceLoading === 'error' ? (
            <Box sx={{ p: { xs: pxToRem(20), md: pxTovW(40) } }}>
              <NoContentCard
                variant="error"
                icon="error"
                text="Error Occured"
              />
            </Box>
          ) : (
            <Box>
              {chapter_resources &&
              chapter_resources.categoryResourceMap.length > 0 ? (
                <>
                  {chapter_resources.categoryResourceMap.map(
                    (resource, resourceIndex) => (
                      <SectionListWithTopCarousel
                        key={resourceIndex}
                        title={resource.categoryTitle}
                        subtitle={resource.categoryDescription}
                        itemsToShow={isIpadOnly ? 2 : 2}
                        containerMdWidth={
                          isIpadOnly ? pxTovW(590) : pxTovW(478)
                        }
                        items={resource.categoryResources
                          .slice()
                          .sort((a, b) => {
                            const isAVideo =
                              a.resourceType ===
                              Resource_ResourceEnum.RESOURCE_TYPE_VIDEO;
                            const isBVideo =
                              b.resourceType ===
                              Resource_ResourceEnum.RESOURCE_TYPE_VIDEO;

                            if (isAVideo && !isBVideo) return -1;
                            if (!isAVideo && isBVideo) return 1;
                            return 0;
                          })
                          .map((category, categoryIndex) => (
                            <ContentDetailCard
                              approvalStatus={
                                category?.schoolContentInfo?.approvalInfo
                                  ?.approvalStatus
                              }
                              resourceStatus={
                                category.resourceCategoryType ===
                                  ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES &&
                                category.createdBy ===
                                  user_info?.teacherProfileId && (
                                  <ResourceStatus
                                    status={
                                      category?.schoolContentInfo?.approvalInfo
                                        ?.approvalStatus
                                    }
                                    handleOpenPopup={(
                                      ev?: React.MouseEvent
                                    ) => {
                                      ev?.stopPropagation();
                                      setRejectedResourcePopupOpen(true);
                                      setSelectedDataForDelete({
                                        resourceId: category.resourceId,
                                        resourceTitle: category.title,
                                      });
                                      if (
                                        category?.schoolContentInfo
                                          ?.approvalInfo
                                      ) {
                                        setApprovalInfo(
                                          category?.schoolContentInfo
                                            ?.approvalInfo
                                        );
                                      }
                                    }}
                                  />
                                )
                              }
                              isLocked={
                                isContentLockFeature &&
                                contentLockData &&
                                contentLockData?.moduleUpdatedLockInfo?.lockedResourceIds?.includes(
                                  category.resourceId
                                )
                              }
                              locked={
                                isContentLockFeature &&
                                (category.resourceCategoryType ===
                                  ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES &&
                                category.createdBy ===
                                  user_info?.teacherProfileId
                                  ? category?.schoolContentInfo?.approvalInfo
                                      ?.approvalStatus ===
                                    ContentApprovalStatusEnum.CONTENT_APPROVAL_STATUS_APPROVED
                                  : true) && (
                                  <LockToggleButton
                                    isLocked={getCategoryLockedStatus(
                                      category.resourceId,
                                      contentLockData
                                    )}
                                    handleOpenPopup={async (ev) => {
                                      handleOpenPopup(ev, undefined, category);
                                      await interactionEvent({
                                        context: resource.categoryTitle.replace(
                                          ' ',
                                          '_'
                                        ),
                                        name: 'CHANGE_LOCK_STATUS',
                                        pathSegments: pathSegments,
                                        isOffline: isOffline,
                                      });
                                    }}
                                  />
                                )
                              }
                              onClick={async () => {
                                await interactionEvent({
                                  context: resource.categoryTitle.replace(
                                    ' ',
                                    '_'
                                  ),
                                  name: 'RESOURCE_OPEN',
                                  pathSegments: pathSegments,
                                  isOffline: isOffline,
                                });
                                category?.schoolContentInfo?.approvalInfo
                                  ?.approvalStatus ===
                                ContentApprovalStatusEnum.CONTENT_APPROVAL_STATUS_REJECTED
                                  ? setRejectedResourcePopupOpen(true)
                                  : onResourceClick(
                                      navigate,
                                      {
                                        resourceId: category.resourceId,
                                        subjectId: Number(subject_id),
                                        chapterId: Number(chapter_id),
                                        approvalStatus:
                                          category?.schoolContentInfo
                                            ?.approvalInfo?.approvalStatus,
                                      },
                                      SessionModeEnum.SESSION_MODE_RESOURCE
                                    );
                              }}
                              key={categoryIndex}
                              // variant={isIpadOnly ? 'large' : 'small'}
                              variant={'small'}
                              image={getMediaBasePath(
                                category.posterImageUrl,
                                category.resourceCategoryType ===
                                  ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                                  ? 'userDataBucket'
                                  : 'processedMediaBucket'
                              )}
                              heading={category.title}
                              iconDetails={iconDetails(category)}
                              showVideoIcon={
                                category.resourceType ===
                                Resource_ResourceEnum.RESOURCE_TYPE_VIDEO
                              }
                              editResourceFunction={
                                category.resourceType ===
                                  Resource_ResourceEnum.RESOURCE_TYPE_TEACHER_UPLOAD &&
                                category.createdBy ===
                                  user_info?.teacherProfileId
                                  ? {
                                      editResource: () => {
                                        editResource({
                                          resourceId: category.resourceId,
                                          coverTitle: category.title,
                                        });
                                      },
                                      deleteResource: () => {
                                        setSelectedDataForDelete({
                                          resourceId: category.resourceId,
                                          resourceTitle: category.title,
                                        });
                                        setConfirmDeletePopup(true);
                                      },
                                    }
                                  : undefined
                              }
                              rootStyle={{
                                width: {
                                  xs: pxToRem(283),
                                  md: pxTovW(450),
                                  lg: pxTovW(244),
                                },
                                height: {
                                  xs: pxToRem(102),
                                  md: pxTovW(150),
                                  lg: pxTovW(88),
                                },
                              }}
                            />
                          ))}
                      />
                    )
                  )}
                </>
              ) : (
                <NoContentCard
                  variant="soon"
                  icon="hourglass-web"
                  text="Coming Soon!"
                  rootStyle={{
                    height: { xs: pxToRem(150), md: pxTovW(212) },
                    boxSizing: 'border-box',
                    mt: { xs: pxToRem(20), md: pxTovW(40) },
                  }}
                />
              )}
            </Box>
          )
        ) : null}
      </Box>
    </Box>
  );
};

const iconDetails = (resource: ResourceInfo) => {
  const retValue = [
    {
      iconName: 'clock',
      text: `${resource.estimatedTimeInMin} Min`,
    },

    {
      iconName: resourceTypeName(resource.resourceType).icon,
      text: resourceTypeName(resource.resourceType).name,
    },
  ];

  return retValue;
};

const getCategoryLockedStatus = (
  resourceId: string,
  contentLockData?: ContentLockModuleData
) => {
  return contentLockData?.moduleUpdatedLockInfo?.lockedResourceIds?.includes(
    resourceId
  )
    ? ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
    : ContentLockStatusType.CONTENT_LOCK_STATUS_IS_UNLOCKED;
};

const BloomLevelBreakdown = [
  {
    title: 'Retention',
  },
  {
    title: 'Comprehension',
  },
  {
    title: 'Application',
  },
  {
    title: 'Analysis',
  },
];
